import moment from 'moment-timezone'
import { useState, useEffect, useCallback } from 'react'

import { findTodayIndex } from 'shared/utils'

/**
 * Hook that manages the state and behavior of the ScrollToTodayButton.
 * Extracts all logic from the component to make it more presentational.
 *
 * @param {Object} params
 * @param {React.RefObject<HTMLDivElement>} params.containerRef - Ref to the scrollable container
 * @param {Array<{rawDate: string, date: string, events?: unknown[]}>} params.meetings - Array of meeting groups
 * @param {string} params.timezone - Timezone string (e.g., 'America/New_York')
 * @param {moment.Moment} [params.currentTime] - Optional current time moment object for timezone-aware scrolling
 * @returns {Object} Object with buttonPosition, buttonLeft, isTop, and handleScrollToToday
 */
export const useScrollToTodayButton = ({
  containerRef,
  meetings,
  timezone,
  currentTime = null
}) => {
  const [buttonPosition, setButtonPosition] = useState('hidden')
  const [buttonLeft, setButtonLeft] = useState(undefined)

  const getTodayGroup = useCallback(() => {
    const container = containerRef.current
    if (!container) return null

    const now = currentTime || moment().tz(timezone)
    const todayStr = now.format('YYYY-MM-DD')

    // Try to find by data-date attribute first (more reliable)
    const todayGroupByAttr = container.querySelector(`[data-date="${todayStr}"]`)
    if (todayGroupByAttr) return todayGroupByAttr

    // Fallback to index-based lookup
    const todayIndex = findTodayIndex(meetings, timezone, now)
    if (todayIndex === -1) return null

    // Find the first meeting group element (has data-date attribute) to determine offset
    // When onLoadPrevious is null, no top bookend is rendered, so the first child is a meeting group
    // When onLoadPrevious exists, there's a top bookend at index 0, so meeting groups start at index 1
    let firstMeetingGroupIndex = 0
    for (let i = 0; i < container.children.length; i += 1) {
      const child = container.children[i]
      // Check if child has data-date attribute (meeting groups have this, bookends/sentinels don't)
      if (child && (
        (child.hasAttribute && child.hasAttribute('data-date'))
        || (child.getAttribute && child.getAttribute('data-date') !== null)
      )) {
        firstMeetingGroupIndex = i
        break
      }
    }

    // Calculate the correct index: firstMeetingGroupIndex + todayIndex
    const targetIndex = firstMeetingGroupIndex + todayIndex
    if (targetIndex >= container.children.length) return null
    return container.children[targetIndex] || null
  }, [containerRef, meetings, timezone, currentTime])

  // Update button horizontal position based on container center
  const updateButtonLeft = useCallback(() => {
    if (typeof window === 'undefined') return

    const container = containerRef.current
    if (!container) return

    const containerRect = container.getBoundingClientRect()
    const containerCenterX = containerRect.left + containerRect.width / 2
    setButtonLeft(containerCenterX)
  }, [containerRef])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined

    const container = containerRef.current
    if (!container) return undefined

    const todayGroup = getTodayGroup()
    if (!todayGroup) {
      setButtonPosition('hidden')
      // Still update button left position even if today group doesn't exist
      updateButtonLeft()
      return undefined
    }

    // Use IntersectionObserver to detect when today's group enters/leaves viewport
    // This is more performant than scroll event listeners
    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry) return

        if (entry.isIntersecting) {
          // Today's group is visible in the viewport
          setButtonPosition('hidden')
        } else {
          // Today's group is not visible - determine if it's above or below viewport
          const todayRect = entry.boundingClientRect
          const viewportTop = 0
          const viewportBottom = window.innerHeight

          if (todayRect.bottom <= viewportTop) {
            // Today is completely above the viewport
            setButtonPosition('top')
          } else if (todayRect.top >= viewportBottom) {
            // Today is completely below the viewport
            setButtonPosition('bottom')
          } else {
            // Edge case: should be intersecting, but fallback to hidden
            setButtonPosition('hidden')
          }
        }
      },
      {
        // Use viewport as root (default)
        root: null,
        // No threshold - we just need to know if it's intersecting at all
        threshold: 0,
        // Small root margin to account for any edge cases
        rootMargin: '0px'
      }
    )

    intersectionObserver.observe(todayGroup)

    // Update button horizontal position on container resize
    let resizeObserver = null
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        updateButtonLeft()
      })
      resizeObserver.observe(container)
    }

    // Initial button position update
    updateButtonLeft()

    return () => {
      intersectionObserver.disconnect()
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
    }
  }, [getTodayGroup, updateButtonLeft, containerRef])

  return {
    buttonPosition, // 'hidden' | 'top' | 'bottom'
    buttonLeft, // number | undefined
    isTop: buttonPosition === 'top',
    isVisible: buttonPosition !== 'hidden'
  }
}
